import React, { createContext, useContext, useState, useEffect } from "react";
import { api } from "../services/api";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [user, setUser] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clienteActual, setClienteActual] = useState(() => {
    const stored = localStorage.getItem("clienteActual");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Soportar tanto el formato viejo (idcliente) como el nuevo (id)
        const id = parsed.id ?? parsed.idcliente;
        if (id && parsed.nombre !== undefined) {
          return { ...parsed, id };
        }
      } catch (e) {
        console.error("Error parseando clienteActual:", e);
      }
    }
    return null;
  });
  const [tienda, setTienda] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      if (token) {
        try {
          const parts = token.split(".");
          if (parts.length !== 3) {
            if (!cancelled) logout();
            return;
          }

          const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(atob(b64));
          if (Date.now() < payload.exp * 1000) {
            localStorage.setItem("token", token);
            if (!cancelled) setIsAuthenticated(true);

            const response = await api.usuario.getCurrentUser();
            if (cancelled) return;

            if (response.status === "success") {
              if (response.data.newToken) {
                localStorage.setItem("token", response.data.newToken);
                setToken(response.data.newToken);
              }
              setUser(response.data.usuario);
              if (response.data.tienda) {
                setTienda(response.data.tienda);
              }
              if (response.data.clienteActual) {
                const c = response.data.clienteActual;
                const clienteSimplificado = {
                  id: c.id,
                  nombre: c.nombre,
                  idpreciolista: c.idpreciolista ?? null,
                };
                setClienteActual(clienteSimplificado);
                localStorage.setItem("clienteActual", JSON.stringify(clienteSimplificado));
              } else {
                setClienteActual(null);
                localStorage.removeItem("clienteActual");
              }
            } else {
              if (!cancelled) logout();
            }
          } else {
            if (!cancelled) logout();
          }
        } catch (error) {
          if (error.message?.includes("fetch")) {
            if (!cancelled) setLoading(false);
            return;
          }
          if (!cancelled) logout();
        }
      } else {
        if (!cancelled) {
          setIsAuthenticated(false);
          setUser(null);
          setLoading(false);
        }
      }
      if (!cancelled) setLoading(false);
    };

    checkAuth();
    return () => { cancelled = true; };
  }, [token]);

  function login(newToken, clientesDisponibles = []) {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setClientes(clientesDisponibles);

    if (clientesDisponibles.length === 1) {
      const c = clientesDisponibles[0];
      const clienteSimplificado = {
        id: c.id,
        nombre: c.nombre,
        idpreciolista: c.idpreciolista ?? null,
      };
      setClienteActual(clienteSimplificado);
      localStorage.setItem("clienteActual", JSON.stringify(clienteSimplificado));
      // pasar clienteData directo para no depender del estado que aún no actualizó
      selectCliente(c.id, newToken, c);
    }
  }

  // clienteData opcional: si se pasa, evita depender del estado clientes que puede no estar actualizado
  async function selectCliente(clienteId, overrideToken, clienteData) {
    try {
      const trusted = localStorage.getItem("trusted") === "1";
      const res = await api.usuario.selectCliente({ clienteId, trusted });
      if (res.status === "success") {
        const newToken = res.data.token;

        // Usar clienteData pasado directo, o buscar en el estado
        const raw = clienteData || clientes.find(c => c.id === clienteId);
        if (raw) {
          const clienteSimplificado = {
            id: raw.id,
            nombre: raw.nombre,
            idpreciolista: raw.idpreciolista ?? null,
          };
          localStorage.setItem("clienteActual", JSON.stringify(clienteSimplificado));
          setClienteActual(clienteSimplificado);
        }
        localStorage.setItem("token", newToken);
        setToken(newToken);

        return { success: true };
      }
      return { success: false };
    } catch (error) {
      console.error("Error en selectCliente:", error);
      return { success: false, error };
    }
  }

  // Registra al usuario como cliente en el ERP y actualiza el token con el clienteId
  async function registrarCliente(data) {
    try {
      const trusted = localStorage.getItem("trusted") === "1";
      const res = await api.clientes.registrar({ ...data, trusted });
      if (res.status === "success") {
        const newToken = res.data.token;
        const c = res.data.cliente;
        const clienteSimplificado = {
          id: c.id,
          nombre: c.nombre,
          idpreciolista: c.idpreciolista ?? null,
        };
        localStorage.setItem("token", newToken);
        localStorage.setItem("clienteActual", JSON.stringify(clienteSimplificado));
        setClienteActual(clienteSimplificado);
        setToken(newToken);
        return { success: true, erp: res.data.erp };
      }
      return { success: false, message: res.message };
    } catch (error) {
      console.error("Error en registrarCliente:", error);
      return { success: false, error };
    }
  }

  function logout() {
    setToken(null);
    setUser(null);
    setClientes([]);
    setClienteActual(null);
    setTienda(null);
    setIsAuthenticated(false);
    localStorage.removeItem("token");
    localStorage.removeItem("clienteActual");
  }

  return (
    <AuthContext.Provider value={{
      token,
      user,
      setUser,
      login,
      logout,
      loading,
      isAuthenticated,
      clientes,
      clienteActual,
      tienda,
      selectCliente,
      registrarCliente,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
